import { AfterViewInit, Component, OnDestroy, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { PortalModuleCategory } from '@src/app/model/portal-module-category';
import { PermissionsService } from '@src/app/services/permissions/permissions.service';
import { PermissionName } from '@src/app/model/userdata';
import { v4 as uuidv4 } from 'uuid';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { JsonModal } from '@src/app/components/utils/json-modal';

@Component({
  selector: 'app-module-category-list',
  templateUrl: './module-categories.component.html',
  styleUrls: ['./module-categories.component.scss'],
  standalone: false,
})
export class ModuleCategoriesComponent implements AfterViewInit, OnDestroy {
  public displayedColumns: string[] = [];
  public displayedBlueprintColumns: string[] = ['name', 'status', 'orderWeight', 'module-variant-ids'];
  public currentModuleCategories: MatTableDataSource<PortalModuleCategory>
    = new MatTableDataSource<PortalModuleCategory>([]);

  public userHasCUDPermission$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _componentDestroyed$: Subject<void> = new Subject();
  private _moduleCategoryFailMsg: string = 'moduleCategoryList.upload.failure';

  public withArchivedBlueprints: boolean = false;

  readonly sort = viewChild(MatSort);

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _permissionsService: PermissionsService
  ) {
    // seed subject from observable
    const sub = this._userHasCUDPermission$();
    sub.subscribe(this.userHasCUDPermission$);
    // determine view; no actions if permissions not present
    this.userHasCUDPermission$
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(r => this.displayedColumns = r
        ? ['reference', 'order', 'customizable', 'module-blueprints', 'action']
        : ['reference', 'order', 'customizable', 'module-blueprints']
      );

    this.getModuleCategories(this.withArchivedBlueprints);
  }

  public ngAfterViewInit(): void {
    this.currentModuleCategories.sort = this.sort();
  }

  public getModuleCategories(withArchivedBlueprints: boolean): void {
    this._apolloService.getAllModuleCategories$(withArchivedBlueprints)
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((result) => {
        this.currentModuleCategories.data = result;
      });
  }

  public openAddModuleCategoryDialog(): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'moduleCategory',
      new PortalModuleCategory(uuidv4(), 'NAME', 999, false),
      'moduleCategoryList.upload.header',
      'buttons.submit',
      ['id']
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      this._apolloService.upsertModuleCategory$.bind(this._apolloService),
      this._translateService.instant(this._moduleCategoryFailMsg),
      this._apolloService.getAllModuleCategoriesRefresh.bind(this._apolloService)
    );
  }

  public openEditModuleCategoryDialog(moduleCategory: PortalModuleCategory): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'moduleCategory',
      moduleCategory ? moduleCategory : new PortalModuleCategory(),
      'moduleCategoryList.edit.header',
      'buttons.submit',
      ['id']
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      this._apolloService.upsertModuleCategory$.bind(this._apolloService),
      this._translateService.instant(this._moduleCategoryFailMsg),
      this._apolloService.getAllModuleCategoriesRefresh.bind(this._apolloService)
    );
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }

  private _userHasCUDPermission$(): Observable<boolean> {
    return this._permissionsService.observePermission$('access_modules', PermissionName.Update);
  }
}
